import React, { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useParentChildren } from "@/hooks/useParentChildren";
import ChildSelector from "@/components/ChildSelector";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Wallet, Plus, Receipt, CircleDollarSign } from "lucide-react";

const zar = (n) => `R ${Number(n || 0).toLocaleString("en-ZA", { minimumFractionDigits: 2 })}`;
const STATUS_COLOR = {
  paid: "bg-emerald-500/15 text-emerald-500",
  arrears: "bg-red-500/15 text-red-500",
  pending: "bg-amber-500/15 text-amber-500",
};

export default function FeesPage() {
  const { user } = useAuth();
  const isParent = user?.role === "parent";
  const isStudent = user?.role === "student";
  const { selected: selectedChild, children: parentChildren } = useParentChildren();

  const [fees, setFees] = useState([]);
  const [summary, setSummary] = useState({});
  const [students, setStudents] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ student_id: "", term: "1", amount: "", description: "Tuition", due_date: "" });

  const canManage = ["admin", "accountant", "principal"].includes(user?.role);

  const load = async () => {
    // For parents with a selected child, scope fees; otherwise defaults apply.
    const params = isParent && selectedChild ? { student_id: selectedChild.id } : {};
    const [f, s] = await Promise.all([
      api.get("/fees", { params }),
      api.get("/fees/summary"),
    ]);
    setFees(f.data); setSummary(s.data);
  };

  useEffect(() => {
    if (isStudent) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedChild?.id, isStudent]);
  useEffect(() => {
    if (canManage) {
      api.get("/users", { params: { role: "student" } }).then((r) => setStudents(r.data));
    }
  }, [canManage]);

  const childNameById = useMemo(() => {
    const m = {};
    parentChildren.forEach((c) => { m[c.id] = c.name; });
    return m;
  }, [parentChildren]);

  // Student fees board is being reimagined — bespoke student view coming later.
  if (isStudent) return <Navigate to="/homework" replace />;

  const pay = async (fee) => {
    try {
      const { data } = await api.post("/fees/pay", { fee_id: fee.id, method: "paystack_mock", amount: fee.amount });
      toast.success(`Paid — Receipt ${data.receipt_no}`, { description: "Mock Paystack payment successful" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Payment failed");
    }
  };

  const createFee = async () => {
    try {
      await api.post("/fees", { ...form, amount: parseFloat(form.amount), term: parseInt(form.term, 10) });
      toast.success("Fee invoice created");
      setOpen(false);
      setForm({ student_id: "", term: "1", amount: "", description: "Tuition", due_date: "" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="space-y-6" data-testid="fees-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[0.65rem] tracking-[0.2em] uppercase text-muted-foreground">Finance</div>
          <h1 className="text-4xl font-bold tracking-tight">Fees & payments</h1>
          <p className="text-muted-foreground text-sm mt-1">All amounts in ZAR (R). Paystack payments are <span className="text-primary font-medium">MOCKED</span> for demo.</p>
        </div>
        {canManage && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-2xl gap-2" data-testid="fees-create-open"><Plus className="h-4 w-4" /> New invoice</Button>
            </DialogTrigger>
            <DialogContent className="rounded-2xl">
              <DialogHeader><DialogTitle>New fee invoice</DialogTitle></DialogHeader>
              <div className="space-y-3 py-2">
                <div>
                  <Label className="text-xs uppercase tracking-widest text-muted-foreground">Student</Label>
                  <Select value={form.student_id} onValueChange={(v) => setForm({ ...form, student_id: v })}>
                    <SelectTrigger data-testid="fee-student-select" className="rounded-2xl mt-1.5"><SelectValue placeholder="Select student" /></SelectTrigger>
                    <SelectContent>
                      {students.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs uppercase tracking-widest text-muted-foreground">Term</Label>
                    <Select value={form.term} onValueChange={(v) => setForm({ ...form, term: v })}>
                      <SelectTrigger className="rounded-2xl mt-1.5"><SelectValue /></SelectTrigger>
                      <SelectContent>{[1, 2, 3, 4].map((t) => <SelectItem key={t} value={String(t)}>Term {t}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-widest text-muted-foreground">Amount (R)</Label>
                    <Input data-testid="fee-amount" type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="rounded-2xl mt-1.5" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-muted-foreground">Description</Label>
                  <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-2xl mt-1.5" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-muted-foreground">Due date</Label>
                  <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="rounded-2xl mt-1.5" />
                </div>
              </div>
              <DialogFooter>
                <Button onClick={createFee} className="rounded-2xl" data-testid="fee-create-submit">Create</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <ChildSelector />

      <div className="grid gap-4 md:grid-cols-4">
        <Card className="rounded-2xl"><CardContent className="p-6"><div className="text-xs uppercase tracking-widest text-muted-foreground">Total invoiced</div><div className="mt-2 text-3xl font-bold">{zar(summary.total)}</div></CardContent></Card>
        <Card className="rounded-2xl"><CardContent className="p-6"><div className="text-xs uppercase tracking-widest text-emerald-500">Collected</div><div className="mt-2 text-3xl font-bold text-emerald-500">{zar(summary.paid)}</div></CardContent></Card>
        <Card className="rounded-2xl"><CardContent className="p-6"><div className="text-xs uppercase tracking-widest text-red-500">Arrears</div><div className="mt-2 text-3xl font-bold text-red-500">{zar(summary.arrears)}</div></CardContent></Card>
        <Card className="rounded-2xl"><CardContent className="p-6"><div className="text-xs uppercase tracking-widest text-amber-500">Pending</div><div className="mt-2 text-3xl font-bold text-amber-500">{zar(summary.pending)}</div></CardContent></Card>
      </div>

      <Card className="rounded-2xl">
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><Wallet className="h-4 w-4 text-primary" /> Invoices</CardTitle></CardHeader>
        <CardContent className="divide-y divide-border/60">
          {fees.length === 0 && <div className="py-8 text-center text-sm text-muted-foreground">No invoices yet.</div>}
          {fees.map((f) => (
            <div key={f.id} className="py-3 flex items-center gap-3 flex-wrap" data-testid={`fee-row-${f.id}`}>
              <div className="h-10 w-10 rounded-xl bg-primary/10 text-primary grid place-items-center"><CircleDollarSign className="h-5 w-5" /></div>
              <div className="flex-1 min-w-0">
                <div className="font-medium">{f.description} — Term {f.term}</div>
                <div className="text-xs text-muted-foreground">
                  Due {f.due_date}
                  {isParent && childNameById[f.student_id] ? ` · ${childNameById[f.student_id]}` : ` · Student ID ${String(f.student_id).slice(-6)}`}
                </div>
              </div>
              <div className="font-semibold">{zar(f.amount)}</div>
              <Badge className={`rounded-full capitalize ${STATUS_COLOR[f.status] || ""}`}>{f.status}</Badge>
              {f.status !== "paid" ? (
                <Button size="sm" onClick={() => pay(f)} className="rounded-xl" data-testid={`fee-pay-${f.id}`}>Pay now</Button>
              ) : (
                <Badge variant="outline" className="rounded-full gap-1"><Receipt className="h-3 w-3" /> {f.receipt_no}</Badge>
              )}
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
